import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "contacted", "scheduled", "completed"];

const SOURCE_LABELS = { city: "City / Municipal", well: "Private Well", not_sure: "Not Sure" };
const BEST_TIME_LABELS = {
  anytime: "Anytime",
  morning: "Morning (8–12)",
  afternoon: "Afternoon (12–5)",
  evening: "Evening (5–8)",
};
const CONCERN_LABELS = {
  hard_water: "Hard Water / Scale / Spots",
  taste_odor: "Taste, Odor & Chlorine",
  skin_hair: "Dry Skin / Hair",
  spots_residue: "Spots & Residue",
  appliances: "Appliance & Pipe Protection",
  drinking_water: "Drinking Water Quality",
  other: "Whole-Home Check",
};

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("ws_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Key": k } });
      setLeads(res.data);
      setAuthed(true);
      localStorage.setItem("ws_admin_key", k);
    } catch {
      setAuthed(false);
      toast.error("Invalid admin key");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: { "X-Admin-Key": key } });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch {
      toast.error("Could not update status");
    }
  };

  if (!authed) {
    return (
      <main className="py-24 bg-[#f8fbff] min-h-[60vh]" data-testid="admin-login">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
            <h1 className="text-xl font-bold text-slate-900">Whitestone Lead Dashboard</h1>
            <p className="text-sm text-slate-600 mt-1">Enter your admin key to view water analysis requests.</p>
            <form
              className="mt-6 space-y-4"
              onSubmit={(e) => { e.preventDefault(); load(key); }}
            >
              <input
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Admin key"
                data-testid="admin-key-input"
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600 bg-slate-50"
              />
              <button type="submit" disabled={loading} data-testid="admin-login-button"
                className="w-full py-3 rounded-xl text-sm font-bold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition disabled:opacity-60">
                {loading ? "Checking..." : "View Leads"}
              </button>
            </form>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="py-16 bg-[#f8fbff] min-h-[60vh]" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Water Analysis Requests</h1>
            <p className="text-sm text-slate-600 mt-1" data-testid="admin-lead-count">{leads.length} lead{leads.length === 1 ? "" : "s"} total</p>
          </div>
          <button
            onClick={() => load(key)}
            data-testid="admin-refresh-button"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#0f2b44] hover:bg-[#1971c2] transition"
          >
            Refresh
          </button>
        </div>

        {leads.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-600" data-testid="admin-empty-state">
            No requests yet. New submissions from the website form will appear here.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto shadow-sm">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Contact</th>
                  <th className="px-5 py-3">City / ZIP</th>
                  <th className="px-5 py-3">Source</th>
                  <th className="px-5 py-3">Concern</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-slate-100 last:border-0" data-testid={`admin-lead-row-${l.id}`}>
                    <td className="px-5 py-3 text-slate-600 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-900">{l.name}</td>
                    <td className="px-5 py-3 text-slate-600">
                      <div>{l.phone}</div>
                      <div className="text-xs">{l.email}</div>
                      <div className="text-xs text-slate-500">Best time: {BEST_TIME_LABELS[l.best_time] || "Anytime"}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{l.city} {l.zip_code}</td>
                    <td className="px-5 py-3 text-slate-600">{SOURCE_LABELS[l.water_source] || l.water_source}</td>
                    <td className="px-5 py-3 text-slate-600">{CONCERN_LABELS[l.concern] || l.concern}</td>
                    <td className="px-5 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`admin-status-select-${l.id}`}
                        className="px-2 py-1.5 rounded-lg border border-slate-300 text-xs bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-600"
                      >
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
